import {
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretRightIcon,
  Key,
  MagnifyingGlassIcon
} from "@phosphor-icons/react";
import {
  type ReactNode,
  useEffect,
  useState
} from "react";
import type { KVBindingRecord } from "../../api/kv/model.ts";
import { useKVExplorer } from "./react.tsx";
import { ResourceTreeAction, ResourceTreeRow } from "../../ResourceExplorerTree";

interface KVExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
  onOpenBrowse: (binding: KVBindingRecord) => void;
}

export function KVExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange,
  onOpenBrowse
}: KVExplorerProps) {
  const { snapshot: { bindings, loadingBindings: loading, error }, dispatch } = useKVExplorer(workspaceId, refreshToken);
  const [expanded, setExpanded] = useState(true);
  const refresh = () => dispatch({ _tag: "Refresh" });
  useEffect(() => { setExpanded(true); }, [workspaceId, refreshToken]);

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            KV
          </button>
        )}
        <ResourceTreeAction label="Refresh KV" onClick={() => void refresh()}>
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId ? (
            bindings.length ? (
              <>
                <ResourceTreeRow
                  depth={0}
                  label={connectionName || "Workerspace"}
                  detail={`${bindings.length} namespace${bindings.length === 1 ? "" : "s"}`}
                  icon={<Key size={14} />}
                  expandable
                  expanded={expanded}
                  onClick={() => setExpanded((value) => !value)}
                />
                {expanded
                  ? bindings.map((binding) => (
                      <ResourceTreeRow
                        key={binding.bindingName}
                        depth={1}
                        label={binding.bindingName}
                        detail={binding.namespace}
                        icon={<Key size={14} weight="fill" />}
                        onClick={() => onOpenBrowse(binding)}
                        onDoubleClick={() => onOpenBrowse(binding)}
                        actions={
                          <ResourceTreeAction
                            label={`Browse ${binding.bindingName}`}
                            onClick={() => onOpenBrowse(binding)}
                          >
                            <MagnifyingGlassIcon size={12} />
                          </ResourceTreeAction>
                        }
                      />
                    ))
                  : null}
              </>
            ) : (
              <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                Declare a KV binding in cloudflare.config.ts, save the
                workerspace, then refresh.
              </div>
            )
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to browse KV.
            </p>
          )}
          {error ? (
            <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
